/** Structure + contents damage for the active scenario (matches BuildingLayer symbology). */
export function buildingDamagesForScenario(
  properties: GeoJSON.GeoJsonProperties | undefined,
  scenarioSuffix: string
): number | null {
  if (!properties) return null

  const structureKey = `structure_damage_value_${scenarioSuffix}`
  const contentsKey = `contents_damage_value_${scenarioSuffix}`
  const rawStructure = properties[structureKey]
  const rawContents = properties[contentsKey]

  if (rawStructure != null || rawContents != null) {
    const structure =
      rawStructure != null && !Number.isNaN(Number(rawStructure)) ? Number(rawStructure) : 0
    const contents =
      rawContents != null && !Number.isNaN(Number(rawContents)) ? Number(rawContents) : 0
    return structure + contents
  }

  for (const key of [`tot_cost_${scenarioSuffix}`, `tot_direct_costs_${scenarioSuffix}`]) {
    const raw = properties[key]
    if (raw != null && !Number.isNaN(Number(raw))) {
      return Number(raw)
    }
  }

  return null
}

function hasScenarioDamageAttributes(
  properties: NonNullable<GeoJSON.GeoJsonProperties>,
  scenarioSuffix: string
): boolean {
  return (
    properties[`structure_damage_value_${scenarioSuffix}`] !== undefined ||
    properties[`contents_damage_value_${scenarioSuffix}`] !== undefined ||
    properties[`tot_cost_${scenarioSuffix}`] !== undefined ||
    properties[`tot_direct_costs_${scenarioSuffix}`] !== undefined
  )
}

/**
 * Scenario runs differ by fractions of a percent; increases at or below this
 * share of baseline are treated as no benefit rather than a genuine worsening.
 */
export const ADAPTATION_INCREASE_NOISE_PCT = 1

export type AdaptationDamageDirection = 'averted' | 'increased' | 'unchanged'

export type AdaptationDamageComparison = {
  baseline: number
  adapted: number
  /** baseline − adapted; positive = averted, negative = additional, zero = unchanged. */
  delta: number
  /** Percent change vs baseline; negative = reduction (benefit). Null if baseline is 0. */
  percentVsBaseline: number | null
  direction: AdaptationDamageDirection
}

/**
 * Baseline vs adapted damages for tooltip / relative benefit framing.
 * When `missingAdaptedAsZero` is true (site supports this adaptation), missing adapted
 * columns match MapLibre coalesce→0 — fully protected buildings still get a comparison.
 */
export function adaptationDamageComparison(
  properties: GeoJSON.GeoJsonProperties | undefined,
  dem: string,
  returnPeriod: string,
  adaptation: string,
  options?: { missingAdaptedAsZero?: boolean }
): AdaptationDamageComparison | null {
  if (!properties || adaptation === '000') return null

  const adaptedSuffix = `dem${dem}_rp${returnPeriod}_adapt${adaptation}`
  const missingAdaptedAsZero = options?.missingAdaptedAsZero === true

  if (!missingAdaptedAsZero && !hasScenarioDamageAttributes(properties, adaptedSuffix)) {
    return null
  }

  const baseline = buildingDamagesForScenario(properties, `dem${dem}_rp${returnPeriod}_adapt000`)
  const adaptedRaw = buildingDamagesForScenario(properties, adaptedSuffix)

  if (baseline == null && adaptedRaw == null) return null
  // Mirror BuildingLayer: absent adapted attrs coalesce to 0 when the scenario is valid.
  if (missingAdaptedAsZero && adaptedRaw == null && baseline == null) return null

  const baselineValue = baseline ?? 0
  const adaptedValue = adaptedRaw ?? 0
  const delta = baselineValue - adaptedValue

  const percentVsBaseline =
    baselineValue > 0 ? ((adaptedValue - baselineValue) / baselineValue) * 100 : null

  let direction: AdaptationDamageDirection
  if (delta > 0) {
    direction = 'averted'
  } else if (
    adaptedValue > 0 &&
    (baselineValue <= 0 ||
      (percentVsBaseline != null && percentVsBaseline > ADAPTATION_INCREASE_NOISE_PCT))
  ) {
    direction = 'increased'
  } else {
    direction = 'unchanged'
  }

  return {
    baseline: baselineValue,
    adapted: adaptedValue,
    delta,
    percentVsBaseline,
    direction,
  }
}
